import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  Users, Globe, BookOpen, Music, Calendar, Award, Heart, Drum,
  Eye, EyeOff, ArrowLeft, Mail, MapPin, Star, ChevronRight, Image, FolderOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon options for programs
const ICON_OPTIONS = ['Music', 'BookOpen', 'Users', 'Calendar', 'Award', 'Heart', 'Globe', 'Drum'];
const IconMap = { Music, BookOpen, Users, Calendar, Award, Heart, Globe, Drum };

// ==================== LIVE PREVIEW COMPONENT ====================
const LivePreview = ({ content }) => {
  if (!content) return null;

  return (
    <div className="bg-white text-gray-900 min-h-full overflow-y-auto">
      {/* Hero Section Preview */}
      <section className="relative py-16 bg-gradient-to-br from-green-600 via-green-700 to-yellow-600 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-yellow-400 to-blue-500" />
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-black text-white mb-2">
            {content.hero?.title || "Casa Samba"}
          </h1>
          <p className="text-lg text-white/90 mb-3">
            {content.hero?.subtitle}
          </p>
          <p className="text-sm text-white/80 max-w-xl mx-auto">
            {content.hero?.description}
          </p>
        </div>
      </section>

      {/* Mission Section Preview */}
      {content.mission?.title && (
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">OUR PURPOSE</span>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{content.mission?.title}</h2>
            <p className="text-gray-600">{content.mission?.description}</p>
          </div>
        </section>
      )}

      {/* History Section Preview */}
      {content.history?.title && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">
              SINCE {content.history?.founded}
            </span>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{content.history?.title}</h2>
            <p className="text-gray-600 mb-4">{content.history?.description}</p>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Founded by</p>
                <p className="text-green-600 text-sm">{content.history?.foundedBy}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Programs Section Preview */}
      {content.programs?.items?.length > 0 && (
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">OUR PROGRAMS</span>
              <h2 className="text-2xl font-bold text-gray-900">{content.programs?.title}</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {content.programs.items.map((program, i) => {
                const Icon = IconMap[program.icon] || Music;
                return (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">{program.title}</h3>
                    <p className="text-gray-600 text-xs">{program.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Leadership Section Preview */}
      {content.leadership?.leaders?.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">MEET THE TEAM</span>
              <h2 className="text-2xl font-bold text-gray-900">{content.leadership?.title}</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {content.leadership.leaders.map((leader, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                  {leader.image ? (
                    <img loading="lazy" decoding="async" src={leader.image} alt={leader.name} className="w-full h-24 object-cover" />
                  ) : (
                    <div className="w-full h-24 bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center">
                      <Users className="w-8 h-8 text-white/50" />
                    </div>
                  )}
                  <div className="p-3">
                    <h3 className="font-bold text-gray-900 text-sm">{leader.name}</h3>
                    <p className="text-green-600 text-xs mb-1">{leader.role}</p>
                    <p className="text-gray-600 text-xs line-clamp-2">{leader.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Members Section Preview */}
      {content.members?.longStandingMembers?.length > 0 && (
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">OUR COMMUNITY</span>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{content.members?.title}</h2>
              <p className="text-gray-600 text-sm">{content.members?.description}</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {content.members.longStandingMembers.map((member, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="w-8 h-8 mx-auto mb-1 rounded-full bg-gradient-to-br from-green-500 to-yellow-500 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">{member.name?.charAt(0)}</span>
                  </div>
                  <p className="font-bold text-gray-900 text-xs">{member.name}</p>
                  {member.role && <p className="text-green-600 text-[10px]">{member.role}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Legacy Link Preview */}
      <section className="py-12 px-4 bg-gradient-to-r from-green-600 to-yellow-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-bold text-white mb-2">Discover the Legacy</h2>
          <p className="text-white/90 text-sm mb-4">
            Learn about Mestre Curtis Pierre, the visionary behind Escola de Samba Casa Samba.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-700 font-bold text-sm rounded-full">
            <Star className="w-4 h-4" />
            The Legacy of Mestre Curtis Pierre
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </section>

      {/* Contact Section Preview */}
      {content.contact?.email && (
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-green-600 font-bold tracking-wider text-xs mb-2 block">GET IN TOUCH</span>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{content.contact?.title}</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {content.contact?.email && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm">
                  <Mail className="w-4 h-4 text-green-600" />
                  <span className="text-gray-900">{content.contact.email}</span>
                </div>
              )}
              {content.contact?.location && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-gray-900">{content.contact.location}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// ==================== EDITOR COMPONENTS ====================
const CollapsibleSection = ({ title, children, defaultOpen = false, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-green-500" />}
          <h3 className="text-sm font-bold text-white">{title}</h3>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-zinc-800 pt-3">
          {children}
        </div>
      )}
    </div>
  );
};

const InputField = ({ label, value, onChange, placeholder, type = 'text', textarea = false }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-zinc-400">{label}</label>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:border-green-500 focus:outline-none resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:border-green-500 focus:outline-none"
      />
    )}
  </div>
);

const ImageField = ({ label, value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-zinc-400">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or select from library"
          className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:border-green-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="px-3 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-500 rounded-lg flex items-center gap-2 transition-colors"
        >
          <FolderOpen className="w-4 h-4" />
          <span className="text-xs font-medium">Browse</span>
        </button>
      </div>
      {value && (
        <div className="mt-2 relative w-24 h-16 rounded-lg overflow-hidden bg-zinc-800">
          <img loading="lazy" decoding="async" 
            src={value.startsWith('http') ? value : `${API_URL}${value}`} 
            alt="" 
            className="w-full h-full object-cover" 
          />
        </div>
      )}
      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(img) => {
          onChange(img.url);
          setShowPicker(false);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

const SelectField = ({ label, value, onChange, options }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-zinc-400">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
    >
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

// ==================== MAIN COMPONENT ====================
export default function AdminCasaSambaAboutEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/casa-samba-about`);
      const data = await response.json();
      setContent(data);
    } catch (error) {
      console.error('Error fetching content:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/casa-samba-about`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        toast.success('Content saved successfully!');
        setMessage({ type: 'success', text: 'Saved!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
      setMessage({ type: 'error', text: 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const resetContent = async () => {
    if (!window.confirm('Reset to default content? This cannot be undone.')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/casa-samba-about/reset`, { method: 'POST' });
      const data = await response.json();
      setContent(data.content);
      toast.success('Content reset to defaults');
    } catch (error) {
      toast.error('Failed to reset content');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateArrayItem = (section, arrayField, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (section, arrayField, template) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: [...(prev[section][arrayField] || []), template]
      }
    }));
  };

  const removeArrayItem = (section, arrayField, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Failed to load content</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-[1920px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="p-2 hover:bg-zinc-800 rounded-lg" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                Escola de Samba Casa Samba About Editor
              </h1>
              <p className="text-xs text-zinc-400">Edit with live preview</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {message && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {message.text}
              </motion.div>
            )}
            
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                showPreview ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-zinc-800 hover:bg-zinc-700'
              }`}
              data-testid="toggle-preview-btn"
            >
              {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {showPreview ? 'Preview On' : 'Preview Off'}
            </button>
            
            <button
              onClick={resetContent}
              className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-2 transition-colors text-sm"
              data-testid="reset-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            
            <button
              onClick={saveContent}
              disabled={saving}
              className="px-5 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 text-sm"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className={`flex ${showPreview ? '' : ''}`}>
        {/* Editor Panel */}
        <div className={`${showPreview ? 'w-1/2' : 'w-full max-w-4xl mx-auto'} p-4 space-y-4 overflow-y-auto`} style={{ height: 'calc(100vh - 64px)' }}>
          
          {/* Hero Section */}
          <CollapsibleSection title="Hero Section" defaultOpen={true} icon={Globe}>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Title" value={content.hero?.title} onChange={(v) => updateField('hero', 'title', v)} />
              <ImageField label="Image URL" value={content.hero?.image} onChange={(v) => updateField('hero', 'image', v)} />
            </div>
            <div className="mt-3">
              <InputField label="Subtitle" value={content.hero?.subtitle} onChange={(v) => updateField('hero', 'subtitle', v)} />
            </div>
            <div className="mt-3">
              <InputField label="Description" value={content.hero?.description} onChange={(v) => updateField('hero', 'description', v)} textarea />
            </div>
          </CollapsibleSection>

          {/* Mission Section */}
          <CollapsibleSection title="Mission Section" icon={Heart}>
            <InputField label="Title" value={content.mission?.title} onChange={(v) => updateField('mission', 'title', v)} />
            <div className="mt-3">
              <InputField label="Description" value={content.mission?.description} onChange={(v) => updateField('mission', 'description', v)} textarea />
            </div>
          </CollapsibleSection>

          {/* History Section */}
          <CollapsibleSection title="History Section" icon={Calendar}>
            <div className="grid grid-cols-3 gap-3">
              <InputField label="Title" value={content.history?.title} onChange={(v) => updateField('history', 'title', v)} />
              <InputField label="Founded Year" value={content.history?.founded} onChange={(v) => updateField('history', 'founded', v)} />
              <InputField label="Founded By" value={content.history?.foundedBy} onChange={(v) => updateField('history', 'foundedBy', v)} />
            </div>
            <div className="mt-3">
              <InputField label="Description" value={content.history?.description} onChange={(v) => updateField('history', 'description', v)} textarea />
            </div>
          </CollapsibleSection>

          {/* Programs Section */}
          <CollapsibleSection title="Programs / What We Do" icon={BookOpen}>
            <InputField label="Section Title" value={content.programs?.title} onChange={(v) => updateField('programs', 'title', v)} />
            
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-white">Programs</span>
                <button
                  onClick={() => addArrayItem('programs', 'items', { title: 'New Program', description: 'Description', icon: 'Music' })}
                  className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1 hover:bg-green-500/30"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              
              {content.programs?.items?.map((program, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-lg p-3 mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-zinc-500">#{i + 1}</span>
                    <button onClick={() => removeArrayItem('programs', 'items', i)} className="text-red-400 hover:bg-red-500/20 p-1 rounded">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <InputField label="Title" value={program.title} onChange={(v) => updateArrayItem('programs', 'items', i, 'title', v)} />
                    <SelectField label="Icon" value={program.icon} onChange={(v) => updateArrayItem('programs', 'items', i, 'icon', v)} options={ICON_OPTIONS} />
                  </div>
                  <div className="mt-2">
                    <InputField label="Description" value={program.description} onChange={(v) => updateArrayItem('programs', 'items', i, 'description', v)} textarea />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Leadership Section */}
          <CollapsibleSection title="Leadership Team" icon={Award}>
            <InputField label="Section Title" value={content.leadership?.title} onChange={(v) => updateField('leadership', 'title', v)} />
            
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-white">Leaders</span>
                <button
                  onClick={() => addArrayItem('leadership', 'leaders', { name: 'New Leader', role: 'Role', bio: '', image: '' })}
                  className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1 hover:bg-green-500/30"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              
              {content.leadership?.leaders?.map((leader, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-lg p-3 mb-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-zinc-500">#{i + 1}</span>
                    <button onClick={() => removeArrayItem('leadership', 'leaders', i)} className="text-red-400 hover:bg-red-500/20 p-1 rounded">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <InputField label="Name" value={leader.name} onChange={(v) => updateArrayItem('leadership', 'leaders', i, 'name', v)} />
                    <InputField label="Role" value={leader.role} onChange={(v) => updateArrayItem('leadership', 'leaders', i, 'role', v)} />
                  </div>
                  <div className="mt-2">
                    <ImageField label="Image URL" value={leader.image} onChange={(v) => updateArrayItem('leadership', 'leaders', i, 'image', v)} />
                  </div>
                  <div className="mt-2">
                    <InputField label="Bio" value={leader.bio} onChange={(v) => updateArrayItem('leadership', 'leaders', i, 'bio', v)} textarea />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Members Section */}
          <CollapsibleSection title="Long-Standing Members" icon={Users}>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Section Title" value={content.members?.title} onChange={(v) => updateField('members', 'title', v)} />
            </div>
            <div className="mt-3">
              <InputField label="Description" value={content.members?.description} onChange={(v) => updateField('members', 'description', v)} textarea />
            </div>
            
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-white">Members</span>
                <button
                  onClick={() => addArrayItem('members', 'longStandingMembers', { name: 'New Member', role: '', years: '' })}
                  className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1 hover:bg-green-500/30"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {content.members?.longStandingMembers?.map((member, i) => (
                  <div key={i} className="bg-zinc-800/50 rounded-lg p-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-zinc-500">#{i + 1}</span>
                      <button onClick={() => removeArrayItem('members', 'longStandingMembers', i)} className="text-red-400 hover:bg-red-500/20 p-1 rounded">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <InputField label="Name" value={member.name} onChange={(v) => updateArrayItem('members', 'longStandingMembers', i, 'name', v)} />
                    <div className="mt-2">
                      <InputField label="Role" value={member.role} onChange={(v) => updateArrayItem('members', 'longStandingMembers', i, 'role', v)} />
                    </div>
                    <div className="mt-2">
                      <InputField label="Years" value={member.years} onChange={(v) => updateArrayItem('members', 'longStandingMembers', i, 'years', v)} placeholder="Since 1995" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>

          {/* Contact Section */}
          <CollapsibleSection title="Contact Information" icon={Mail}>
            <div className="grid grid-cols-2 gap-3">
              <InputField label="Section Title" value={content.contact?.title} onChange={(v) => updateField('contact', 'title', v)} />
              <InputField label="Email" value={content.contact?.email} onChange={(v) => updateField('contact', 'email', v)} type="email" />
              <InputField label="Phone" value={content.contact?.phone} onChange={(v) => updateField('contact', 'phone', v)} />
              <InputField label="Location" value={content.contact?.location} onChange={(v) => updateField('contact', 'location', v)} />
            </div>
          </CollapsibleSection>

          {/* Save Button at Bottom */}
          <div className="pt-4 pb-8">
            <button
              onClick={saveContent}
              disabled={saving}
              className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="save-bottom-btn"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>

        {/* Live Preview Panel */}
        {showPreview && (
          <div className="w-1/2 border-l border-zinc-800 bg-zinc-950" style={{ height: 'calc(100vh - 64px)' }}>
            <div className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800 px-4 py-2 flex items-center justify-between">
              <span className="text-xs font-bold text-zinc-400 flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-500" />
                LIVE PREVIEW
              </span>
              <span className="text-[10px] text-zinc-500">Changes appear instantly</span>
            </div>
            <div className="overflow-y-auto" style={{ height: 'calc(100vh - 104px)' }}>
              <LivePreview content={content} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
